import React, { useState, useEffect } from 'react';
import './StrawberryAnimation.css';

/**
 * BirthdayPage component for displaying birthday greetings and videos
 */
const BirthdayPage = () => {
  // State for collapsible thoughts section
  const [showThoughts, setShowThoughts] = useState(false);
  const [sasToken, setSasToken] = useState("");
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  // New state for countdown timer
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false
  });

  // Get SAS token from environment variables when component mounts
  useEffect(() => {
    // Make sure SAS token starts with "?" if it doesn't already
    const token = process.env.REACT_APP_AZURE_STORAGE_SAS_TOKEN || "sv=2024-11-04&ss=b&srt=sco&sp=rlaf&se=2026-09-01T00:45:42Z&st=2025-06-04T16:45:42Z&spr=https&sig=6%2BEVE%2BbmtrgrRKNdZZwNo34ZGkc95udVqzst6OXuFM0%3D";
    setSasToken(token.startsWith("?") ? token : `?${token}`);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    // Target date: 19 July 2025, 10:00 AM UK time
    const targetDate = new Date('2025-07-19T10:00:00+01:00').getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      
      // Check if countdown has expired
      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      
      // Calculate time units
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds, expired: false });
    };
    
    // Initial update
    updateCountdown();
    
    // Set interval to update countdown every second
    const interval = setInterval(updateCountdown, 1000);
    
    // Clear interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Azure blob storage configuration
  const STORAGE_BASE_URL = "https://ukskristinasa.blob.core.windows.net/birthday2025";

  // Helper function to generate blob URLs with SAS token
  const toBlobUrl = (filename) => {
    const encodedPath = encodeURIComponent(filename);
    // This ensures the SAS token is properly appended as a query string
    return `${STORAGE_BASE_URL}/${encodedPath}${sasToken}`;
  };

  // Video data with optional comments
  const videos = [
    { 
      filename: "IMG_8647.MP4", 
      title: "From Pakistan, with love",
      comment: ""
    },
    { 
      filename: "km_20250530_1080p_30f_20250530_200030.mp4", 
      title: "From Maggie the cow, with love",
      comment: "I think Maggie's owner, Yousaf, has a crush on you ;)"
    },
    { 
      filename: "VN20250530_123532.mp4", 
      title: "From my dear friend (his words), Ameen, with love",
      comment: ""
    }
  ];

  // Flip the card
  const flipCard = () => {
    setIsCardFlipped(!isCardFlipped);
  };

  // Styles object
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      color: 'white',
      minHeight: '100vh',
      textAlign: 'center',
      position: 'relative',
      background: 'linear-gradient(135deg, #280a14 0%, #450e22 50%, #280a14 100%)',
    },
    header: {
      textAlign: 'center',
      marginBottom: '40px',
      width: '100%',
      position: 'relative',
    },
    title: {
      fontSize: '2.5rem',
      color: '#FF69B4',
      marginBottom: '10px',
      textShadow: '0 0 10px rgba(255, 105, 180, 0.5)',
    },
    subtitle: {
      fontSize: '1.2rem',
      color: 'white',
      fontWeight: 'normal',
    },
    videosSection: {
      marginBottom: '40px',
      textAlign: 'center',
      width: '100%',
      position: 'relative',
    },
    videoContainer: {
      marginBottom: '30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      transition: 'transform 0.3s ease',
    },
    videoTitle: {
      fontSize: '1.5rem',
      marginBottom: '10px',
      color: 'white',
      textAlign: 'center',
    },
    video: {
      width: '100%',
      maxWidth: '600px',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.3), 0 0 20px rgba(255, 105, 180, 0.3)',
      margin: '0 auto',
      border: '2px solid rgba(255, 105, 180, 0.3)',
      transition: 'box-shadow 0.3s ease',
    },
    commentContainer: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      padding: '15px',
      borderRadius: '8px',
      marginTop: '10px',
      width: '100%',
      maxWidth: '600px',
      textAlign: 'center',
      backdropFilter: 'blur(5px)',
    },
    commentText: {
      fontSize: '1rem',
      lineHeight: '1.5',
      color: 'white',
      fontStyle: 'italic',
      margin: '0 auto',
    },
    cardContainer: {
      width: '100%',
      maxWidth: '600px',
      height: '400px',
      perspective: '1000px',
      marginBottom: '40px',
      position: 'relative',
    },
    card: {
      width: '100%',
      height: '100%',
      position: 'relative',
      transition: 'transform 0.8s',
      transformStyle: 'preserve-3d',
      transform: isCardFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
      cursor: 'pointer',
    },
    cardFace: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backfaceVisibility: 'hidden',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cardFront: {
      background: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)',
      color: 'white',
    },
    cardBack: {
      background: 'rgba(255, 105, 180, 0.1)',
      backdropFilter: 'blur(10px)',
      transform: 'rotateY(180deg)',
      overflowY: 'auto',
    },
    cardTitle: {
      fontSize: '1.8rem',
      marginBottom: '20px',
      textAlign: 'center',
    },
    strawberryIcon: {
      fontSize: '5rem',
      marginBottom: '20px',
    },
    thoughtItem: {
      margin: '10px 0',
      fontSize: '1.1rem',
      lineHeight: '1.6',
      color: 'white',
      textAlign: 'center',
    },
    footer: {
      textAlign: 'center',
      color: 'white',
      fontSize: '1rem',
      padding: '20px 0',
      width: '100%',
      position: 'relative',
    },
    heart: {
      color: '#FF69B4',
    },
    surpriseSection: {
      marginTop: '60px',
      marginBottom: '60px',
      textAlign: 'center',
      width: '100%',
      position: 'relative',
      padding: '20px',
      backgroundColor: 'rgba(255,105,180,0.1)',
      borderRadius: '15px',
      backdropFilter: 'blur(5px)',
    },
    surpriseTitle: {
      fontSize: '2rem',
      color: '#FF69B4',
      marginBottom: '15px',
      textShadow: '0 0 10px rgba(255, 105, 180, 0.5)',
    },
    surpriseSubtext: {
      fontSize: '1.3rem',
      color: 'white',
      marginBottom: '30px',
      fontStyle: 'italic',
    },
    countdownContainer: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '15px',
      marginBottom: '30px',
    },
    countdownItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: '80px',
      padding: '10px',
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: '8px',
    },
    countdownNumber: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#FF69B4',
    },
    countdownLabel: {
      fontSize: '0.9rem',
      color: 'white',
      textTransform: 'uppercase',
    },
    expiredMessage: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#FF69B4',
      padding: '15px',
      marginBottom: '30px',
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: '8px',
      animation: 'pulse 2s infinite',
    },
    teaserImage: {
      maxWidth: '500px',
      width: '100%',
      height: 'auto',
      borderRadius: '10px',
      boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 0 15px rgba(255, 105, 180, 0.4)',
      marginTop: '20px',
      border: '3px solid rgba(255, 105, 180, 0.3)',
      display: 'block',
      margin: '20px auto 10px',
    },
    imageCaption: {
      fontSize: '0.9rem',
      color: 'white',
      fontStyle: 'italic',
      marginTop: '10px',
      textAlign: 'center',
      opacity: '0.8',
      maxWidth: '450px',
      margin: '10px auto 0',
      lineHeight: '1.4',
    },
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <header style={styles.header}>
        <h1 style={styles.title}>Happy Birthday Kristina 🎉</h1>
        <h3 style={styles.subtitle}>3 silly videos. 1 cheesy message. All for you.</h3>
      </header>

      {/* Videos Section */}
      <section style={styles.videosSection}>
        {videos.map((video, index) => (
          <div key={index} style={styles.videoContainer} className="video-box">
            <h2 style={styles.videoTitle}>{video.title}</h2>
            <video 
              controls 
              style={styles.video}
              src={toBlobUrl(video.filename)}
              className="video-element"
            >
              Your browser does not support the video tag.
            </video>
            {video.comment && (
              <div style={styles.commentContainer}>
                <p style={styles.commentText}>{video.comment}</p>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Interactive Strawberry Card for Thoughts */}
      <div style={styles.cardContainer} onClick={flipCard}>
        <div style={styles.card} className="strawberry-card">
          <div style={{...styles.cardFace, ...styles.cardFront}} className="card-front">
            <div style={styles.strawberryIcon}>🧀</div>
            <h2 style={styles.cardTitle}>Tap to Read My Cheese</h2>
          </div>
          <div style={{...styles.cardFace, ...styles.cardBack}} className="card-back">
            <h2 style={styles.cardTitle}>I love...</h2>
            <p style={styles.thoughtItem}>waking up next to you.</p>
            <p style={styles.thoughtItem}>how you cuddle your head into me, like you're exactly where you're meant to be.</p>
            <p style={styles.thoughtItem}>how the world goes quiet when you put your hand on me.</p>
            <p style={styles.thoughtItem}>how your soft eyes and smile disarm me — even when I'm trying to be serious.</p>
            <p style={styles.thoughtItem}>that you love my ass even more than I do.</p>
            <p style={styles.thoughtItem}>your softness — even when you play the cool girl (you are cool, by the way).</p>
            <p style={styles.thoughtItem}></p>
            <p style={styles.thoughtItem}><b>Most importantly...</b></p>
            <p style={styles.thoughtItem}>There is no other person I'd rather grow with.</p>

            

            <p style={styles.thoughtItem}><b>I love you.</b></p>
          </div>
        </div>
      </div>

      {/* Surprise Section with Countdown */}
      <section style={styles.surpriseSection}>
        <h2 style={styles.surpriseTitle}>OFFICIAL NOTICE: Rally Participation Confirmed</h2>
        <p style={styles.surpriseSubtext}>The Department of Birthday Adventures hereby informs you that participation in an off-road experience is mandatory. Mud-to-clothing ratio will exceed recommended levels. Complaints are not accepted. Bribes of sexual nature, however, may be considered.”</p>
        
        {countdown.expired ? (
          <div style={styles.expiredMessage}>
            Pack your boots. It's go time.
          </div>
        ) : (
          <div style={styles.countdownContainer}>
            <div style={styles.countdownItem}>
              <span style={styles.countdownNumber}>{countdown.days}</span>
              <span style={styles.countdownLabel}>Days</span>
            </div>
            <div style={styles.countdownItem}>
              <span style={styles.countdownNumber}>{countdown.hours}</span>
              <span style={styles.countdownLabel}>Hours</span>
            </div>
            <div style={styles.countdownItem}>
              <span style={styles.countdownNumber}>{countdown.minutes}</span>
              <span style={styles.countdownLabel}>Minutes</span>
            </div>
            <div style={styles.countdownItem}>
              <span style={styles.countdownNumber}>{countdown.seconds}</span>
              <span style={styles.countdownLabel}>Seconds</span>
            </div>
          </div>
        )}
        
        <div>
          <img 
            src={toBlobUrl("CA49B405-4CA1-4636-901E-7D860883D1D7.jpg")}
            style={styles.teaserImage}
            alt="Adventure teaser"
          />
          <p style={styles.imageCaption}>
            © 2025 FutureCap Media Ltd. | Kristina expertly navigating the famed Muddy Corner during the 
            July 2025 Yorkshire Rally Championships. This authenticated image was captured using 
            quantum-temporal photography and is certified 100% accurate by the International Institute 
            of Predictive Imagery. Not staged or digitally enhanced.*
          </p>
        </div>
      </section>
    </div>
  );
};

export default BirthdayPage;